/**
 * "Build with AI" for subtask templates (backend `api/ai/template-draft`). Injected into the
 * AI slice (own file, so it doesn't touch AiSlice.ts). Saving uses the existing
 * `api/work-template/add-template` and refreshes the templates list.
 */
import type {
  DraftMode,
  DraftTeamMember,
  DraftWorkType,
  ServerDraft,
} from "../features/settings/templates/taskTemplates/aiBuilder/templateDraft.utils";
import { toAddTemplatePayload } from "../features/settings/templates/taskTemplates/aiBuilder/templateDraft.utils";
import { aiSlice } from "./AiSlice";
import { templatesSlice } from "./TemplatesSlice";

export type TemplateDraftRequest = {
  orgId: string;
  mode: DraftMode;
  text?: string;
  workTypeId?: string | null;
  includeDueDays: boolean;
  includeRoles: boolean;
  currentDraft?: ServerDraft;
  instruction?: string;
};

export type TemplateDraftResponse = ServerDraft & { warnings: string[] };

export const templateDraftApi = aiSlice.injectEndpoints({
  endpoints: (build) => ({
    getTemplateDraftOptions: build.query<
      { workTypes: DraftWorkType[]; team: DraftTeamMember[] },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: "api/ai/template-draft/options",
        params: { orgId },
      }),
      keepUnusedDataFor: 0,
    }),
    aiTemplateDraft: build.mutation<
      TemplateDraftResponse,
      TemplateDraftRequest
    >({
      query: (body) => ({ url: "api/ai/template-draft", method: "POST", body }),
      invalidatesTags: (result) => (result ? ["AiStatus"] : []),
    }),
    saveTemplateDraft: build.mutation<
      { id: string },
      ReturnType<typeof toAddTemplatePayload>
    >({
      query: (body) => ({
        url: "api/work-template/add-template",
        method: "POST",
        body,
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(templatesSlice.util.invalidateTags(["Template"]));
        } catch {
          // The window shows the error.
        }
      },
    }),
  }),
});

export const {
  useGetTemplateDraftOptionsQuery,
  useAiTemplateDraftMutation,
  useSaveTemplateDraftMutation,
} = templateDraftApi;
